// This file handles refreshing our auth cookies.  The way our auth
// scheme works is the user has a long-lived KAAS ("session") cookie
// that gives them the right to be logged in.  But the only way to use
// that KAAS cookie is to ask for a short-lived KAAL ("login") cookie
// that actually allows them access the site as themselves.  (The KAAL
// cookie contains a kaid in its payload.)  The user has to get a new
// KAAL cookie once every 5 minutes or they will lose all access to
// their user data and be treated as a logged-out user.
//
// (The reason for this scheme is we can revoke permissions by
// deleting the datastore entity associated with the KAAL cookie,
// which will automatically deny that user permission next time they
// try to get a new KAAL cookie.)
//
// This file provides the mechanism for making sure the user has
// a valid KAAL cookie before making any API call:
//
// First, it knows how to talk to the server with the graphql mutation
// that takes in a KAAS cookie value and gives back a set-cookie
// header to set the KAAL cookie.  That is the API for renewing the
// KAAL cookie.
//
// Second, it is hooked into khanFetch so anytime there is a network
// request (for someone who is logged in), it checks if it's asked for
// the KAAL cookie recently.  If not, it does the asking before
// proceeding with the original fetch.
//
// It has one optimization: it tries to pre-fetch the KAAL cookie, so
// the khanFetch doesn't block waiting for us to refresh the cookie
// whenever it expires.  If an API call is made while the KAAL cookie
// is still valid, but within 1 minute of it expiring, it sends the
// refresh request in parallel to the requested API request.
// Hopefully the refresh request will return within 60 seconds, and
// we'll never have to block waiting for a new KAAL cookie.
//
// Note that the browser does not actually have access to the KAAL
// cookie, which it's set to be httponly.  That is why we use
// hard-coded knowledge that the KAAL cookie expires every 5 minutes.

import gql from "graphql-tag";

import {setRequireKAALCookie} from "@khan/data-fetch";
import {standaloneGqlFetch} from "@khan/data-gql";
import {gqlOp, type GqlOperation} from "@khan/data-gql-core";
import {PermissionsInfo} from "@khan/shared-core";

import type {AuthCookieMutationType} from "./__graphql-types__/index.ts";

// sync-start:kaac-cookie-lifetime 1405245990 https://github.com/Khan/webapp/blob/master/services/fastly-khanacademy-compute/middlewares/authentication.go
// We subtract a few seconds here because what matters is how
// much time has expired by the time our request gets to the
// server, and that can take a few seconds.  We'll give it 10.
const COOKIE_LIFETIME = 5 * 60 - 10;
// sync-end:kaac-cookie-lifetime
const COOKIE_REFRESH_SLACK = 50; // refresh ~1 minute before expiry

const COOKIE_LIFETIME_MS = COOKIE_LIFETIME * 1000;
const COOKIE_REFRESH_SLACK_MS = COOKIE_REFRESH_SLACK * 1000;

export const _REFRESH_MUTATION: GqlOperation<
    AuthCookieMutationType["response"],
    AuthCookieMutationType["variables"]
> = gqlOp(gql`
    mutation AuthCookieMutation {
        refreshAuthCookies {
            error
        }
    }
`);

// This is exported only for use by tests.
export class _CookieRefresher {
    lastCookieRefresh: number;
    activeFetchPromise: Promise<boolean> | null | undefined;

    constructor() {
        this.lastCookieRefresh = 0;
        this.activeFetchPromise = null;
    }

    // We don't need to refetch cookies if we're a logged-out user.
    // But we do if we're logged in or if we're a phantom.  The surest
    // way to check for those is if there's a KAAC cookie present --
    // in theory that is set when you log in or become a phantom, and
    // is cleared when you log out.  We also check for isLoggedIn,
    // just in case the user's KAAC cookie got deleted entirely but
    // should still be renewed.  (Ideally we'd check for the existence
    // of the KAAS cookie, which is the source of truth that we are
    // logged in and should be renewing auth cookies, but the browser
    // does not have access to that cookie.  We use isLoggedIn as a
    // proxy for the KAAS cookie.)
    shouldRefetchCookies(): boolean {
        return (
            (document &&
                document.cookie &&
                document.cookie.indexOf("KAAC=") !== -1) ||
            PermissionsInfo.isLoggedIn
        );
    }

    doCookieRefresh(): Promise<boolean> {
        // If we're already doing this refresh, just return the
        // existing promise, don't create a new one.
        if (this.activeFetchPromise == null) {
            // We're just ignoring refresh errors, both transport
            // errors (where the mutate call throws) and application
            // errors (where `error` is not "ok".).
            // TODO(WEB-1359): We could try to handle errors intelligently.
            // TODO(csilvers): if we fail with "cannot renew because
            // session expired" or "user not found" or whatever, set
            // shouldRefetchCookies() to false.
            // NOTE: This is permitted here because we cannot wait for a React
            // component tree to get a safe client.
            // eslint-disable-next-line no-restricted-syntax -- see above
            this.activeFetchPromise = standaloneGqlFetch(_REFRESH_MUTATION)
                .then(() => {
                    // Wait until the mutation comes back (with the
                    // set-cookie headers in tow) before we say that the
                    // cookies have been refreshed.
                    this.activeFetchPromise = null;
                    this.lastCookieRefresh = Date.now();
                    return true; // no error!
                })
                .catch((error) => {
                    this.activeFetchPromise = null;
                    return false;
                });
        }
        return this.activeFetchPromise;
    }

    requireKAALCookie(request: Request): Promise<Request> {
        // If you're not logged in, you don't need (and won't have)
        // any auth cookies.
        if (!this.shouldRefetchCookies()) {
            return Promise.resolve(request);
        }

        // The refresh-query itself doesn't require a KAAL cookie!
        if (request && request.url.includes("AuthCookieMutation")) {
            return Promise.resolve(request);
        }

        const timeToExpiry =
            this.lastCookieRefresh + COOKIE_LIFETIME_MS - Date.now();

        if (timeToExpiry > COOKIE_REFRESH_SLACK_MS) {
            // If the KAAL cookie won't expire in a long time, we
            // don't need to do anything.
            return Promise.resolve(request);
        }
        if (timeToExpiry > 0) {
            // The cookie is about to expire, let's ask for a new one
            // but we don't need to wait for it yet.
            this.doCookieRefresh();
            return Promise.resolve(request);
        }
        // The cookie has expired, we need to wait for a new one.
        // We try twice in case the first one fails.
        const retryIfNeeded = (success: boolean) =>
            success ? Promise.resolve(true) : this.doCookieRefresh();
        return (
            this.doCookieRefresh()
                .then(retryIfNeeded)
                .then(() => request)
                // (This catch() probably isn't needed since we catch()
                // in doCookieRefresh itself, but it can't hurt!)
                .catch(() => request)
        );
    }
}

// This is the public API used in prod -- it works on a singleton
// refresher.

const gCookieRefresher = new _CookieRefresher();

// Start loading the KAAL cookie directly on page load, because we're
// going to need it as soon as we start making API calls.
export function init() {
    if (gCookieRefresher.shouldRefetchCookies()) {
        gCookieRefresher.doCookieRefresh();
    }

    /**
     * We attach requireKAALCookie to break the circular dependency of
     * khan-fetch.js -> cookie-refresh.js -> apollo-fetch.js ->
     *     create-client.js -> khan-fetch.js
     * This is called by CookieRefresh.init() inside site-infra.js.
     */
    setRequireKAALCookie(requireKAALCookie);
}

export function requireKAALCookie(request: Request): Promise<Request> {
    return gCookieRefresher.requireKAALCookie(request);
}
